import { useEffect } from 'react';

import { push } from 'connected-react-router';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import GenericError from 'src/app/components/Error/Generic';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import TextInput from 'src/app/components/Form/TextInput';
import { GenericPasswordError } from 'src/app/components/Pages/Password/Change/utils';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { forgotPasswordSuccess, logout } from 'src/app/router/paths';
import { segmentAnalyticsEvent } from 'src/app/state/actions';
import { FORGOT_PASSWORD_FORM_SUBMIT_BUTTON } from 'src/consts/testIDs';

import { Container, Header, SubmitButton, Form } from '../styles';

import validation from './validation';

const ResetPassword = () => {
  useSegmentEvent('viewForgotPassword', { shouldTrackOnMount: true });

  const { isLoggedIn, forgotPassword } = useAuth();

  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(push(`${logout.toPath()}?initiated=true`));
    }
  }, [dispatch, isLoggedIn]);

  // _generic are form-wide errors, not field-specific errors. We don't want isValid to be false
  // if _generic is the only form error

  return (
    <Container>
      <Header>Don’t worry, let’s reset your password</Header>

      <Formik
        initialValues={validation.initialValues}
        validationSchema={validation.schema}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          const { email } = values;

          dispatch(segmentAnalyticsEvent(UNIFIED.requestPasswordReset.name));
          setSubmitting(true);

          try {
            await forgotPassword(email);
            dispatch(segmentAnalyticsEvent(UNIFIED.requestPasswordSuccessful.name));
            setSubmitting(false);
            dispatch(push(forgotPasswordSuccess.toPath()));
          } catch (error: any) {
            dispatch(segmentAnalyticsEvent(UNIFIED.requestPasswordFailed.name));
            setErrors(error?.message ?? GenericPasswordError);
            setSubmitting(false);
          }
        }}
      >
        {({ isValid, isSubmitting }) => (
          <Form>
            <GenericError />
            <SegmentFormIncomplete />

            <TextInput name="email" type="email" label="Email" />

            <SubmitButton
              disabled={!isValid}
              isSubmitting={isSubmitting}
              data-testid={FORGOT_PASSWORD_FORM_SUBMIT_BUTTON}
            >
              RESET PASSWORD
            </SubmitButton>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default ResetPassword;
